import PropTypes from "prop-types";
import { MathJax } from "better-react-mathjax";

import ActionButton from "../../formInputs/buttons/ActionButton";
import DcsdDialog from "../../modals/DcsdDialog";
import TextField from "../../formInputs/TextField";
import isEprEditable from "../../../utils/ActionControls/isEprEditable";
import { getColorClass, RATING_DISPLAY_LABELS } from "../../../utils/teacher/InputRatingUtils";

import "../../../styles/RatingTable.scss";

/**
 * Per-cell rating comment modal for the batch rating grid.
 *
 * Purely presentational — all state lives in the parent (RatingGrid).
 * Opens when a teacher clicks the comment icon on a rated cell.
 *
 * @param {boolean}  props.open
 * @param {Function} props.onClose
 * @param {Function} props.onSave
 * @param {Object}   props.context    - { student, contentItem, rating, comment, key }
 * @param {Function} props.setContext - updates comment text in place
 * @param {boolean}  props.isActiveWindow
 * @param {string}   props.mode       - "HOMEROOM" | "GROUPS"
 * @param {Object}   props.selectedEpr
 * @param {Object}   props.userDetails
 */
const CommentModal = ({
    open,
    onClose,
    onSave,
    context,
    setContext,
    isActiveWindow,
    mode,
    selectedEpr,
    userDetails
}) => {
    if (!context) return null;

    const { student, contentItem, rating, comment } = context;
    const savedContentItem = student.contentRatings.find((cr) => cr.key === context.key);
    const profileStatus = mode === "GROUPS" ? student.gradingProfileStatus : selectedEpr?.status;

    const canEditComment = isEprEditable({ isActiveWindow, status: profileStatus, userDetails });

    const disableSave =
        !rating ||
        student.isDisabled ||
        !canEditComment ||
        (comment.trim().length === 0 && (savedContentItem?.comment === null || savedContentItem?.comment === ""));

    return (
        <DcsdDialog
            id="rating-comment-modal"
            ariaLabel="Edit Rating Comment"
            title={`Edit Comment for ${student?.firstName} ${student?.lastName}`}
            open={open ? "rating-comment-modal" : ""}
            onHide={onClose}
            hasCloseX
            actions={
                <>
                    <ActionButton className="action-button-cancel" label="Cancel" onClick={onClose} />
                    <ActionButton disable={disableSave} label="Save Comment" onClick={() => onSave(context)} />
                </>
            }
        >
            <div className="rating-comment-model">
                <div className="section-spacing">
                    <MathJax hideUntilTypeset="first" tabIndex={-1}>
                        <strong>Content Item:</strong> <span dangerouslySetInnerHTML={{ __html: contentItem.label }} />
                    </MathJax>
                </div>
                <div className="section-spacing">
                    <strong>Current Rating:</strong>{" "}
                    <span className={`${getColorClass(rating)}-comment`}>{RATING_DISPLAY_LABELS[rating] || "--"}</span>
                </div>
                <div className="section-spacing">
                    <strong>Content Item Rating Comment:</strong>
                    <TextField
                        charsUsed={comment.length}
                        className="form-control"
                        disabled={student.isDisabled || !canEditComment}
                        maxLimit={7500}
                        name="body"
                        id="subject"
                        onChange={(e) => setContext({ ...context, comment: e.target.value })}
                        placeholder="Please add your comment"
                        value={comment}
                    />
                </div>
                {(!rating || student.isDisabled) && (
                    <div className="error-container">
                        {student.isDisabled && (
                            <span className="error-text">You do not have edit permission for this student.</span>
                        )}
                        {!rating && !student.isDisabled && (
                            <span className="error-text">Content item must have a rating to add a comment.</span>
                        )}
                    </div>
                )}
            </div>
        </DcsdDialog>
    );
};

CommentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    context: PropTypes.shape({
        student: PropTypes.object,
        contentItem: PropTypes.object,
        rating: PropTypes.string,
        comment: PropTypes.string,
        key: PropTypes.string
    }),
    setContext: PropTypes.func.isRequired,
    isActiveWindow: PropTypes.bool,
    mode: PropTypes.string,
    selectedEpr: PropTypes.object,
    userDetails: PropTypes.object
};

export default CommentModal;
